import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Tab,
  TextField,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  FormControlLabel,
  Switch,
  Modal,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Div from "@jumbo/shared/Div";
import { Formik } from "formik";
import * as Yup from "yup";
import { makeFormikYupValidator } from "app/utils/validation";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { suppressQuillDeprecationWarnings } from "app/utils/consoleSuppression";
import { useSnackbar } from "notistack";
import {
  getCategoryBannerImages,
  deleteCategoryBannerImage,
  generateCategoryBannerImage,
} from "app/services/api/category";
import CategoryIcon from "./CategoryIcon";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Images from "app/pages/media/images/Images";
import { PROD_URL } from "app/services/config";
// import { getImages } from "app/services/api/media";

const CategoryDetailsTab = ({
  category,
  languages,
  defaultLanguage,
  onEditCategory,
  categories,
  loading,
  aiCategoryImageGenerationActive,
}) => {
  // Suppress Quill.js deprecation warnings
  React.useEffect(() => {
    const restoreWarn = suppressQuillDeprecationWarnings();
    return restoreWarn; // Restore original console.warn on cleanup
  }, []);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthUser } = useJumboAuth();
  const { customerId } = getAuthUser() || {};
  const { enqueueSnackbar } = useSnackbar();
  const [languageTab, setLanguageTab] = useState(
    defaultLanguage || (languages && languages[0])
  );
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [baseURL, setBaseURL] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const normalizeBoolean = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val === 1;
    if (typeof val === "string")
      return val.toLowerCase() === "true" || val === "1";
    return false;
  };
  useEffect(() => {
    if (defaultLanguage) {
      setLanguageTab(defaultLanguage);
    }
  }, [defaultLanguage]);

  const handleLanguageTabChange = (event, newValue) => {
    setLanguageTab(newValue);
  };

  useEffect(() => {
    let mounted = true;
    const loadImages = async () => {
      try {
        if (!imagePickerOpen) return;
        const resp = await getCategoryBannerImages();
        const baseURL = resp?.baseURL || "";
        const list = resp?.images || [];
        if (mounted) {
          setAvailableImages(list || []);
          setBaseURL(baseURL);
        }
      } catch (e) {
        if (mounted) setAvailableImages([]);
      }
    };
    loadImages();
    return () => {
      mounted = false;
    };
  }, [imagePickerOpen]);

  // Don't render the form if no languages are available
  if (!languages || languages.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {t("common.noLanguagesAvailable")}
        </Typography>
      </Box>
    );
  }

  const initialValues = {
    _id: category?._id || null,
    name: category?.name || {},
    description: category?.description || {},
    image: category?.image || "",
    icon: category?.icon || "",
    customIcon: category?.customIcon || "",
    include_in_menu: category?.include_in_menu,
    isActive:
      category?.isActive !== undefined
        ? normalizeBoolean(category?.isActive)
        : true,
    customCategoryId: category?.customCategoryId || "",
    categoryCode: category?._id || "",
    menuType: category?.menuType || "",
    parentCategory: category?.parentCategory || "",
    products: category?.products || {},
    enableAiCategoryImageGeneration: normalizeBoolean(
      category?.enableAiCategoryImageGeneration
    ),
    aiImage: {
      category:
        category?.aiImage?.category ||
        category?.aiCategory ||
        t("pages.dsokCategories.defaultAiCategory", {
          defaultValue: "Hlavní jídla",
        }),
      locale: category?.aiImage?.locale || category?.aiLocale || "cs",
      seed: category?.aiImage?.seed ?? category?.aiSeed ?? 2,
      style:
        category?.aiImage?.style ||
        category?.aiStyle ||
        "slovak cuisine, sirloin on cream, pork dumpling very, goulash with dumpling, fried schnitzel with potato salad, modern table, professional food photography, realistic, appetizing, vibrant, restaurant, wide shot, no menus, no boards, no backgrounds with text, only food and table",
      negative_prompt:
        category?.aiImage?.negative_prompt ||
        category?.aiNegativePrompt ||
        "text, words, letters, menu, menu board, chalkboard, blackboard, signage, poster, label, brand, logo, watermark, captions, subtitles, typography, price tags, numbers, symbols, receipts, magazines, newspapers, packaging, boxes, cans, bottles with labels",
    },
  };

  const DSOKCategorySchema = Yup.object().shape({
    name: Yup.object().test(
      "at-least-one-language",
      t("validation.atLeastOneLanguage"),
      (value) => {
        if (!value) return false;
        return Object.values(value).some((val) => val && val.trim() !== "");
      }
    ),
  });

  const handleSubmit = async (values) => {
    try {
      await onEditCategory(values);
      // Redirect to DSOK categories page after successful save
      navigate("/customer/dsok/categories");
    } catch (error) {
      // Error handling is done in the parent component
      console.error("Error saving category:", error);
    }
  };
  const handleDeleteImage = async (fileName) => {
    try {
      await deleteCategoryBannerImage(fileName);
      enqueueSnackbar(
        `${t("buttons.delete")} ${t("notifications.wasSuccessful")}`,
        {
          variant: "success",
        }
      );
      setAvailableImages((prev) => (prev || []).filter((f) => f !== fileName));
    } catch (e) {
      enqueueSnackbar(
        `${t("buttons.delete")} ${t("notifications.wasFailed")}`,
        {
          variant: "error",
        }
      );
    }
  };
  let enableSystemCategoryImage = true;
  let enableAiImageGenerator = false;
  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Formik
          validateOnChange={true}
          validateOnSubmit={true}
          enableReinitialize={true}
          initialValues={initialValues}
          key={category?._id || "new"}
          validate={makeFormikYupValidator(DSOKCategorySchema, (missing) => {
            alert(
              `${t("messages.checkRequiredFields")}\n` +
                missing.map((p) => `• ${p}`).join("\n")
            );
          })}
          onSubmit={(values, { setSubmitting, setTouched }) => {
            // Mark all fields as touched to show validation errors
            setTouched({
              name: true,
            });
            handleSubmit(values).finally(() => {
              setSubmitting(false);
            });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            if (
              aiCategoryImageGenerationActive &&
              !values.enableAiCategoryImageGeneration
            ) {
              enableSystemCategoryImage = true;
              enableAiImageGenerator = false;
            }
            if (
              aiCategoryImageGenerationActive &&
              values.enableAiCategoryImageGeneration
            ) {
              enableSystemCategoryImage = false;
              enableAiImageGenerator = true;
            }
            return (
              <form
                onSubmit={(e) => {
                  handleSubmit(e);
                }}
              >
                <Grid container spacing={4}>
                  {/* Language Tabs */}
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    {/* Category Code */}
                    <Grid item xs={12} md={6} lg={3}>
                      <TextField
                        fullWidth
                        label={t("pages.dsokCategories.categoryCode")}
                        name="categoryCode"
                        value={values.categoryCode}
                        size="small"
                        disabled={true}
                      />
                    </Grid>
                    {/* Status (isActive) - First item */}
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(values.isActive)}
                            onChange={(e) =>
                              setFieldValue("isActive", e.target.checked)
                            }
                            name="isActive"
                            color="primary"
                          />
                        }
                        label={t("pos.status", { defaultValue: "Status" })}
                      />
                    </Grid>
                    {/* Category Image */}
                    {aiCategoryImageGenerationActive && (
                      <Grid item xs={12} md={12} lg={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.enableAiCategoryImageGeneration}
                              onChange={(e) =>
                                setFieldValue(
                                  "enableAiCategoryImageGeneration",
                                  e.target.checked
                                )
                              }
                              name="enableAiCategoryImageGeneration"
                              color="primary"
                            />
                          }
                          label={t(
                            "pages.dsokCategories.enableAiCategoryImageGeneration"
                          )}
                        />
                      </Grid>
                    )}
                    {enableAiImageGenerator && (
                      <Grid item xs={12} md={6} lg={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t("pages.dsokCategories.image")}
                        </Typography>
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 140,
                            border: values.image
                              ? "1px solid transparent"
                              : "2px dashed",
                            borderColor: values.image ? "divider" : "divider",
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backgroundColor: values.image
                              ? "transparent"
                              : "action.hover",
                          }}
                          onClick={() => setImagePickerOpen(true)}
                          title={t("buttons.selectFromGallery")}
                        >
                          {values.image ? (
                            <Box sx={{ position: "relative", width: "100%" }}>
                              <Avatar
                                src={
                                  values.image?.includes &&
                                  values.image.includes("http")
                                    ? values.image
                                    : typeof values.image === "string"
                                    ? values.image
                                    : URL.createObjectURL(values.image)
                                }
                                variant="rounded"
                                sx={{
                                  height: "auto",
                                  width: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                  borderRadius: 1,
                                }}
                              />
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setFieldValue("image", "");
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  bgcolor: "#fff",
                                  border: "1px solid #ccc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 14,
                                  lineHeight: 1,
                                  cursor: "pointer",
                                  boxShadow: 1,
                                }}
                                title="Remove image"
                              >
                                ×
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t("buttons.selectFromGallery")}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t(
                              "pages.dsokDetails.customerUISettings.tabs.aiSettings"
                            )}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t("pages.dsokCategories.ai.category")}
                                value={values.aiImage?.category || ""}
                                onChange={(e) =>
                                  setFieldValue(
                                    "aiImage.category",
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t("pages.dsokCategories.ai.locale")}
                                value={values.aiImage?.locale || ""}
                                onChange={(e) =>
                                  setFieldValue(
                                    "aiImage.locale",
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={t("pages.dsokCategories.ai.seed")}
                                value={values.aiImage?.seed ?? 0}
                                onChange={(e) =>
                                  setFieldValue(
                                    "aiImage.seed",
                                    Number(e.target.value)
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t("pages.dsokCategories.ai.style")}
                                multiline
                                minRows={2}
                                value={values.aiImage?.style || ""}
                                onChange={(e) =>
                                  setFieldValue("aiImage.style", e.target.value)
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label={t(
                                  "pages.dsokCategories.ai.negativePrompt"
                                )}
                                multiline
                                minRows={2}
                                value={values.aiImage?.negative_prompt || ""}
                                onChange={(e) =>
                                  setFieldValue(
                                    "aiImage.negative_prompt",
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                disabled={isGenerating}
                                onClick={async () => {
                                  try {
                                    setIsGenerating(true);
                                    const payload = {
                                      category: values.aiImage?.category,
                                      locale: values.aiImage?.locale,
                                      seed: values.aiImage?.seed,
                                      style: values.aiImage?.style,
                                      negative_prompt:
                                        values.aiImage?.negative_prompt,
                                    };
                                    await generateCategoryBannerImage(payload);
                                    setImagePickerOpen(true);
                                  } catch (e) {
                                    enqueueSnackbar(
                                      t("notifications.wasFailed") ||
                                        "Generation failed",
                                      { variant: "error" }
                                    );
                                  } finally {
                                    setIsGenerating(false);
                                  }
                                }}
                              >
                                {isGenerating
                                  ? t("common.generating") || "Generating..."
                                  : t("buttons.generate") || "Generate"}
                                {isGenerating && (
                                  <CircularProgress
                                    size={18}
                                    sx={{ ml: 1, color: "inherit" }}
                                  />
                                )}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                    {enableSystemCategoryImage && (
                      <Grid item xs={12} md={6} lg={3}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t("pages.dsokCategories.image")}
                        </Typography>
                        <UploadComponent
                          name="image"
                          setFieldValue={setFieldValue}
                          value={values.image}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          <Alert variant="outlined" severity="info">
                            {t("pages.dsokCategories.categoryImageSizeInfo")}
                          </Alert>
                        </Typography>
                      </Grid>
                    )}
                    {/* Category Icon */}
                    <Grid item xs={12} md={6} lg={3}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("pages.dsokCategories.icon")}
                      </Typography>
                      <CategoryIcon
                        name="icon"
                        setFieldValue={setFieldValue}
                        value={values.icon}
                      />
                      {values.customIcon ? (
                        <Box sx={{ mt: 1 }}>
                          <Avatar
                            src={
                              typeof values.customIcon === "string"
                                ? values.customIcon
                                : ""
                            }
                            variant="rounded"
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              bgcolor: "#C0844D",
                            }}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}></Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <TabContext value={languageTab}>
                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            mb: 3,
                          }}
                        >
                          <TabList
                            onChange={handleLanguageTabChange}
                            aria-label="language tabs"
                          >
                            {languages.map((lang) => (
                              <Tab
                                key={lang}
                                label={t(`languages.${lang}`)}
                                value={lang}
                              />
                            ))}
                          </TabList>
                        </Box>

                        {languages.map((lang) => (
                          <TabPanel key={lang} value={lang} sx={{ p: 0 }}>
                            <Grid container spacing={3}>
                              {/* Category Name */}
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label={t("pages.dsokCategories.name")}
                                  name={`name.${lang}`}
                                  value={values.name[lang] || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={Boolean(errors.name)}
                                  helperText={errors.name}
                                  size="small"
                                  sx={{ mb: 2 }}
                                  disabled={loading}
                                />
                              </Grid>

                              {/* Category Identifiers */}
                              {(values.customCategoryId || values.menuType) && (
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      mt: -1,
                                      mb: 1,
                                    }}
                                  >
                                    {values.customCategoryId && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        Custom ID: {values.customCategoryId}
                                      </Typography>
                                    )}
                                    {values.menuType && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        Menu type: {values.menuType}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              )}

                              {/* Category Description */}
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                  {t("pages.dsokCategories.description")}
                                </Typography>
                                <Box
                                  sx={{
                                    "& .ql-editor": {
                                      minHeight: "120px",
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                    },
                                  }}
                                >
                                  <ReactQuill
                                    value={values.description[lang] || ""}
                                    onChange={(content) => {
                                      setFieldValue(
                                        `description.${lang}`,
                                        content
                                      );
                                    }}
                                    placeholder={
                                      t(
                                        "pages.dsokCategories.descriptionPlaceholder"
                                      ) || "Enter category description..."
                                    }
                                    modules={{
                                      toolbar: [
                                        // [{ header: [1, 2, false] }],
                                        ["bold", "italic", "underline"],
                                        // ["link"],
                                        // [{ list: "ordered" }, { list: "bullet" }],
                                        // ["clean"],
                                      ],
                                    }}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </TabPanel>
                        ))}
                      </TabContext>
                    </Grid>
                  </Grid>

                  {/* Include in Menu */}
                  {/* <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.include_in_menu || false}
                      onChange={(e) =>
                        setFieldValue("include_in_menu", e.target.checked)
                      }
                      name="include_in_menu"
                    />
                  }
                  label={t("pages.dsokCategories.includeInMenu")}
                />
              </Grid> */}

                  {/* Divider */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} container spacing={3}>
                    <Grid item xs={12} md={12} lg={12}></Grid>
                    {/* Menu Type */}
                    {/* <Grid item xs={12} md={6} lg={2}>
                  <FormControl fullWidth size="small">
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t("pages.dsokCategories.menuType")}
                    </Typography>
                    <Select
                      value={values.menuType || ""}
                      onChange={handleChange}
                      name="menuType"
                    >
                      <MenuItem value="pickup">{t("common.pickup")}</MenuItem>
                      <MenuItem value="delivery">
                        {t("common.delivery")}
                      </MenuItem>
                      <MenuItem value="dine_in">{t("common.dineIn")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}

                    {/* Parent Category */}
                    {/* <Grid item xs={12} md={6} lg={3}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        {t("pages.dsokCategories.parentCategory")}
                      </Typography>
                      <Autocomplete
                        freeSolo
                        disabled={loading}
                        options={
                          categories?.filter((cat) => cat._id !== values._id) ||
                          []
                        }
                        getOptionLabel={(option) => {
                          if (typeof option === "string") {
                            return option;
                          }
                          return (
                            option.name?.[defaultLanguage] || option.name || ""
                          );
                        }}
                        value={
                          categories?.find(
                            (cat) => cat._id === values.parentCategory
                          ) ||
                          values.parentCategory ||
                          null
                        }
                        onChange={(event, newValue) => {
                          if (typeof newValue === "string") {
                            setFieldValue("parentCategory", newValue);
                          } else if (newValue && newValue._id) {
                            setFieldValue("parentCategory", newValue._id);
                          } else {
                            setFieldValue("parentCategory", "");
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            {option.name?.[defaultLanguage] ||
                              option.name ||
                              ""}
                          </li>
                        )}
                      />
                    </Grid> */}
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Div
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        pt: 3,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || loading}
                        size="large"
                      >
                        {t("buttons.save")}
                      </Button>
                    </Div>
                  </Grid>
                </Grid>
                <Modal
                  open={imagePickerOpen}
                  onClose={() => setImagePickerOpen(false)}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "80vw",
                      maxHeight: "80vh",
                      overflow: "auto",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      boxShadow: 24,
                      p: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("sidebar.menuItem.images")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                      {(availableImages || []).map((img, idx) => {
                        const src = `${baseURL}data/${customerId}/categories/banners/${img}`;
                        if (!src) return null;
                        return (
                          <Box
                            key={idx}
                            sx={{ m: 0.5, width: 120, textAlign: "center" }}
                          >
                            <Avatar
                              src={src}
                              variant="rounded"
                              onClick={() => {
                                setFieldValue("image", src);
                                setImagePickerOpen(false);
                              }}
                              sx={{
                                width: 120,
                                height: 90,
                                cursor: "pointer",
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setImageToDelete(img);
                                setConfirmDeleteOpen(true);
                              }}
                              sx={{ mt: 0.5, minWidth: 120 }}
                            >
                              {t("buttons.delete")}
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                    <Dialog
                      open={confirmDeleteOpen}
                      onClose={() => setConfirmDeleteOpen(false)}
                    >
                      <DialogContent>
                        <Typography variant="body2">
                          {t("widgets.confirmDialog.areYouSureToDelete")}
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          onClick={() => {
                            setConfirmDeleteOpen(false);
                            setImageToDelete(null);
                          }}
                        >
                          {t("buttons.cancel")}
                        </Button>
                        <Button
                          color="error"
                          variant="contained"
                          onClick={async () => {
                            const target = imageToDelete;
                            setConfirmDeleteOpen(false);
                            setImageToDelete(null);
                            if (target) {
                              await handleDeleteImage(target);
                            }
                          }}
                        >
                          {t("buttons.delete")}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                </Modal>
              </form>
            );
          }}
        </Formik>
      )}
    </Box>
  );
};

const UploadComponent = ({ name, setFieldValue, value }) => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customerId } = getAuthUser();
  const [openSelector, setOpenSelector] = useState(false);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "75vw",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
  };

  return (
    <>
      <Box
        onClick={() => setOpenSelector(true)}
        sx={{
          width: "100%",
          minHeight: "160px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 1,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        {value ? (
          <Box sx={{ position: "relative", width: "100%" }}>
            <Avatar
              src={typeof value === "string" ? value : ""}
              variant="rounded"
              sx={{
                height: "auto",
                width: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokPromotions.selectImage", {
              defaultValue: "Click to select image",
            })}
          </Typography>
        )}
      </Box>
      <Modal
        open={!!openSelector}
        onClose={() => setOpenSelector(false)}
        aria-labelledby="modal-image-selector-title"
        aria-describedby="modal-image-selector-description"
      >
        <Div sx={modalStyle}>
          <Images
            layout={"row-reverse"}
            selectorMode
            allowed={["jpg", "jpeg", "png", "svg"]}
            onCancel={() => setOpenSelector(false)}
            onSelect={(image) => {
              const imageUrl = `${PROD_URL}/uploads/files/${customerId}/Images/${image.filename}`;
              setFieldValue(name, imageUrl);
              setOpenSelector(false);
            }}
          />
        </Div>
      </Modal>
    </>
  );
};

export default CategoryDetailsTab;
